import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { me, logout } from "../../lib/api";
import { formatInputDateUTC } from "../../lib/date";
import { useAtom } from "jotai";
import { DarkModeState } from "../../Jotai/index";
import {
  fetchAllMagazines,
  presignMagazineUpload,
  uploadToPresignedUrlWithProgress,
  updateMagazine,
  deleteMagazine,
  uploadServerSideMagazine,
  replaceMagazine,
  replaceMagazinePage,
} from "../../lib/api";
import CoverThumb from "../../components/magazines/CoverThumb";

export default function MagazinesDashboard() {
  const navigate = useNavigate();
  const [authChecked, setAuthChecked] = useState(false);
  useEffect(() => {
    let alive = true;
    (async () => {
      const r = await me();
      if (!alive) return;
      if (!r.ok) {
        const redirect = encodeURIComponent(window.location.pathname + window.location.search);
        navigate(`/admin/login?redirect=${redirect}`, { replace: true });
      } else {
        setAuthChecked(true);
      }
    })();
    return () => { alive = false; };
  }, [navigate]);
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(8);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  
  // Search state
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [searching, setSearching] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const searchInputRef = useRef(null);

  const [title, setTitle] = useState("");
  const [date, setDate] = useState("");
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);
  const [uploadStatus, setUploadStatus] = useState("");

  const load = async (p = page, ps = pageSize, { noCache = false } = {}) => {
    setLoading(true);
    setErr(null);
    try {
      const res = await fetchAllMagazines({ page: p, pageSize: ps, noCache });
      setItems(res.items || []);
      setPage(res.page || p);
      setPageSize(res.pageSize || ps);
      setTotalPages(res.totalPages || 1);
    } catch (e) {
      setErr(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (authChecked) load(1, 8); }, [authChecked]); // eslint-disable-line

  // Debounce search input
  useEffect(() => {
    const id = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(id);
  }, [search]);

  // Helper: fetch all magazines (paginate) for client-side search
  const fetchAllForSearch = async () => {
    const first = await fetchAllMagazines({ page: 1, pageSize: 60 });
    let all = first.items || [];
    const total = first.totalPages || 1;
    for (let p = 2; p <= total; p++) {
      const res = await fetchAllMagazines({ page: p, pageSize: 60 });
      if (res.items?.length) all = all.concat(res.items);
    }
    return all;
  };

  // Run search when debounced value changes
  useEffect(() => {
    let alive = true;
    const run = async () => {
      if (!debouncedSearch) {
        setSearchResults([]);
        setSearching(false);
        return;
      }
      setSearching(true);
      try {
        const all = await fetchAllForSearch();
        if (!alive) return;
        const q = debouncedSearch.toLowerCase();
        const results = all.filter((it) => {
          const t = (it.title || "").toLowerCase();
          const s = (it.slug || "").toLowerCase();
          const d = it.publishedAt ? formatInputDateUTC(it.publishedAt).toLowerCase() : "";
          return t.includes(q) || s.includes(q) || d.includes(q);
        });
        // Sort by date desc then title
        results.sort((a, b) => {
          const ad = a.publishedAt ? +new Date(a.publishedAt) : 0;
          const bd = b.publishedAt ? +new Date(b.publishedAt) : 0;
          if (bd !== ad) return bd - ad;
          return (a.title || "").localeCompare(b.title || "");
        });
        setSearchResults(results);
      } catch (e) {
        if (!alive) return;
        setErr(e);
      } finally {
        if (!alive) return;
        setSearching(false);
      }
    };
    run();
    return () => { alive = false; };
  }, [debouncedSearch]);

  const inSearch = debouncedSearch.length > 0;
  const visibleItems = inSearch ? searchResults : items;

  const canSubmit = title.trim() && file && /^application\/pdf$/i.test(file.type || "application/pdf");

  const onCreate = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setUploadPct(0);
    setUploadStatus("");
    setErr(null);
    try {
      // Server-side upload: WebP generation + text extraction (no PDF stored)
      await uploadServerSideMagazine({ 
        title: title.trim(), 
        publishedAt: date || undefined, 
        file,
        onProgress: (percent, status) => {
          setUploadPct(Math.round(percent));
          setUploadStatus(status);
          console.log(`${status} ${Math.round(percent)}%`);
        }
      });
      // Clear form
      setTitle("");
      setDate("");
      setFile(null);
      
      // Show completion message
      setUploadPct(100);
      setUploadStatus("Complete! Refreshing list...");
      
      // Refresh the magazine list (go to page 1 to see the newly added magazine)
      await load(1, pageSize, { noCache: true });
      
      // Clear progress bar and submitting state after showing success
      setTimeout(() => {
        setUploadPct(0);
        setUploadStatus("");
        setSubmitting(false);
      }, 1500);
    } catch (e) {
      setErr(e);
      setSubmitting(false);
    }
  };

  const [editing, setEditing] = useState({}); // slug -> { title, date, coverFile, coverKey, coverUploadPct }
  const startEdit = (it) => setEditing((s) => ({ ...s, [it.slug]: { title: it.title, date: it.publishedAt ? formatInputDateUTC(it.publishedAt) : "", coverKey: it.coverKey || "" } }));
  const cancelEdit = (slug) => setEditing((s) => { const n = { ...s }; delete n[slug]; return n; });
  const commitEdit = async (it) => {
    const ed = editing[it.slug];
    if (!ed) return;
    // Optional: upload new cover image if provided
    let coverKey = ed.coverKey || it.coverKey;
    if (ed.coverFile) {
      const ct = ed.coverFile.type || "image/jpeg";
      const pres = await presignMagazineUpload({ title: ed.title || it.title, publishedAt: ed.date || undefined, contentType: ct, coverKey });
      await uploadToPresignedUrlWithProgress(
        pres.uploadUrl,
        ed.coverFile,
        (p) => setEditing((s) => ({ ...s, [it.slug]: { ...s[it.slug], coverUploadPct: p } }))
      );
      coverKey = pres.coverKey;
    }
    await updateMagazine(it.slug, { title: ed.title, publishedAt: ed.date || undefined, coverKey });
    cancelEdit(it.slug);
  await load(page, pageSize, { noCache: true });
  };

  const [confirmSlug, setConfirmSlug] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const onDelete = (slug) => setConfirmSlug(slug);
  const doDelete = async () => {
    if (!confirmSlug) return;
    setDeleting(true);
    try {
      const it = items.find(i => i.slug === confirmSlug);
      const deleteCover = Boolean(it?.coverKey);
      await deleteMagazine(confirmSlug, { deleteFile: true, deleteCover });
      setConfirmSlug(null);
      await load(page, pageSize, { noCache: true });
    } finally {
      setDeleting(false);
    }
  };

  // Replace magazine state
  const [replaceOpen, setReplaceOpen] = useState(false);
  const [replaceForm, setReplaceForm] = useState({ slug: null, file: null, title: "", date: "", pct: 0, status: "", err: null });
  
  const openReplace = (it) => {
    setReplaceForm({
      slug: it.slug,
      file: null,
      title: it.title || "",
      date: it.publishedAt ? formatInputDateUTC(it.publishedAt) : "",
      pct: 0,
      status: "",
      err: null
    });
    setReplaceOpen(true);
  };
  
  const closeReplace = () => {
    setReplaceOpen(false);
    setReplaceForm({ slug: null, file: null, title: "", date: "", pct: 0, status: "", err: null });
  };
  
  const confirmReplace = async () => {
    if (!replaceForm.file || !replaceForm.slug) return;
    
    try {
      setReplaceForm((s) => ({ ...s, pct: 0, status: "", err: null }));
      
      await replaceMagazine(replaceForm.slug, {
        file: replaceForm.file,
        title: replaceForm.title || undefined,
        publishedAt: replaceForm.date || undefined,
        onProgress: (percent, status) => {
          setReplaceForm((s) => ({ ...s, pct: Math.round(percent), status }));
        }
      });
      
      closeReplace();
      await load(page, pageSize, { noCache: true });
    } catch (e) {
      setReplaceForm((s) => ({ ...s, err: e }));
    }
  };

  // Replace single page state
  const [pageReplaceOpen, setPageReplaceOpen] = useState(false);
  const [pageReplaceForm, setPageReplaceForm] = useState({ 
    slug: null, 
    title: "",
    pageCount: 0,
    pageNumber: 1, 
    file: null, 
    pct: 0, 
    status: "", 
    err: null 
  });
  
  const openPageReplace = (it) => {
    setPageReplaceForm({
      slug: it.slug,
      title: it.title || "",
      pageCount: it.pageCount || it.webpKeys?.length || 0,
      pageNumber: 1,
      file: null,
      pct: 0,
      status: "",
      err: null
    });
    setPageReplaceOpen(true);
  };
  
  const closePageReplace = () => {
    setPageReplaceOpen(false);
    setPageReplaceForm({ slug: null, title: "", pageCount: 0, pageNumber: 1, file: null, pct: 0, status: "", err: null });
  };
  
  const confirmPageReplace = async () => {
    if (!pageReplaceForm.file || !pageReplaceForm.slug || pageReplaceForm.pageNumber < 1) return;
    
    try {
      setPageReplaceForm((s) => ({ ...s, pct: 0, status: "", err: null }));
      
      await replaceMagazinePage(pageReplaceForm.slug, pageReplaceForm.pageNumber, {
        file: pageReplaceForm.file,
        onProgress: (percent, status) => {
          setPageReplaceForm((s) => ({ ...s, pct: Math.round(percent), status }));
        }
      });
      
      closePageReplace();
      await load(page, pageSize, { noCache: true });
    } catch (e) {
      setPageReplaceForm((s) => ({ ...s, err: e }));
    }
  };

  const doLogout = async () => {
    try { await logout(); } catch {}
    navigate("/admin/login", { replace: true });
  };
  const [darkMode] = useAtom(DarkModeState);

  if (!authChecked) return null;
  return (
    <main className={`${darkMode ? "darkmode " : ""}admin-page max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8`}>
      <header className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Magazines Dashboard</h1>
          <p className="text-sm text-neutral-600">Create, edit, and manage magazine issues.</p>
          <div className="mt-2 text-sm">
            <Link to="/admin" className="underline">&#8592; Back to Admin Dashboard</Link>
          </div>
        </div>
        <button onClick={doLogout} className="px-4 py-2 rounded text-white h-fit cursor-pointer" style={{ backgroundColor: '#FF9843' }}>Log out</button>
      </header>

  <section className="admin-card mb-10 p-5 border rounded-lg bg-white text-neutral-900">
        <h2 className="text-lg font-medium mb-3 text-neutral-900">Create new magazine</h2>
    <form onSubmit={onCreate} className="grid gap-3">
          <label className="grid gap-1">
            <span className="text-sm text-neutral-700">Title:</span>
  <input type="text" className="admin-input border rounded px-3 py-2 text-black placeholder-neutral-500" value={title} onChange={(e) => setTitle(e.target.value)} required />
          </label>
          <label className="grid gap-1">
            <span className="text-sm text-neutral-700">Publish Date:</span>
  <input type="date" className="admin-input border rounded px-3 py-2 text-black placeholder-neutral-500" value={date} onChange={(e) => setDate(e.target.value)} />
          </label>
          <label className="grid gap-1">
            <span className="text-sm text-neutral-700">PDF File:</span>
  <input type="file" className="text-black" accept="application/pdf" onChange={(e) => setFile(e.target.files?.[0] || null)} />
          </label>
          <div className="flex items-center gap-3">
            <button type="submit" disabled={!canSubmit || submitting} className="px-4 py-2 rounded-md text-white disabled:opacity-50" style={{ backgroundColor: '#FF9843' }}>
              {submitting ? (uploadPct > 0 && uploadPct < 100 ? `Uploading… ${uploadPct}%` : "Uploading…") : "Create"}
            </button>
            {err && <span className="text-sm text-red-600">{err.message || String(err)}</span>}
          </div>
          {submitting && uploadPct > 0 && (
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-neutral-600 font-medium">{uploadStatus || "Processing..."}</span>
                <span className="text-neutral-500">{uploadPct}%</span>
              </div>
              <div className="w-full h-2 bg-neutral-200 rounded overflow-hidden">
                <div className="h-full bg-indigo-600 transition-[width] duration-200" style={{ width: `${uploadPct}%` }} />
              </div>
            </div>
          )}
        </form>
      </section>

      {/* Search below create section */}
  <section className="mb-6">
        <div className="max-w-md">
          <form onSubmit={(e) => { e.preventDefault(); setDebouncedSearch(search.trim()); }}>
            <div
              className="admin-search flex items-center rounded-lg px-3 py-2.5 shadow-sm focus-within:ring-2 border"
              style={{ '--tw-ring-color': '#4cc8f4' }}
            >
              <svg className="h-4 w-4 text-gray-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                ref={searchInputRef}
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search magazines by title..."
                className="flex-1 bg-transparent border-0 ring-0 focus:ring-0 outline-none appearance-none leading-5 placeholder-gray-400"
                style={{ fontSize: window.innerWidth <= 768 ? '16px' : '14px' }}
                inputMode="search"
                enterKeyHint="search"
              />
              {search && (
                <button
                  type="button"
                  onClick={() => { setSearch(""); setDebouncedSearch(""); searchInputRef.current?.focus(); }}
                  className="ml-2 h-5 w-5 flex items-center justify-center text-gray-400 hover:text-gray-600 outline-none border-0 bg-transparent p-0 focus:outline-none focus:ring-0 cursor-pointer"
                  aria-label="Clear search"
                >
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>
          </form>
          {search && (
            <p className="mt-2 text-sm text-gray-600">
              {searching
                ? 'Searching…'
                : (visibleItems.length === 0
                    ? 'No magazines found'
                    : `Found ${visibleItems.length} ${visibleItems.length === 1 ? 'magazine' : 'magazines'}`)}
            </p>
          )}
        </div>
      </section>

      <section>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-medium">{inSearch ? "Search results" : "All magazines"}</h2>
          {!inSearch && (
            <div className="flex items-center gap-2 text-sm">
              <button className="border rounded px-2 py-1 hover:bg-neutral-50 cursor-pointer" disabled={page <= 1 || loading} onClick={() => load(page - 1, pageSize)}>&lt; Prev</button>
              <span>Page {page} / {totalPages}</span>
              <button className="border rounded px-2 py-1 hover:bg-neutral-50 cursor-pointer" disabled={page >= totalPages || loading} onClick={() => load(page + 1, pageSize)}>Next &gt;</button>
            </div>
          )}
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
          {loading ? (
            Array.from({ length: 8 }).map((_, i) => (
              <div key={`skeleton-${i}`} className="admin-card rounded-lg border shadow-sm overflow-hidden bg-white">
                <div className="aspect-[17/22] w-full animate-pulse bg-neutral-200" />
                <div className="p-2 md:p-3">
                  <div className="h-4 w-3/4 bg-neutral-200 rounded mb-2" />
                  <div className="h-3 w-1/2 bg-neutral-200 rounded" />
                </div>
              </div>
            ))
          ) : visibleItems.length ? visibleItems.map((it) => (
            <article key={it.slug} className="admin-card rounded-lg border shadow-sm overflow-hidden bg-white transition hover:shadow-md">
              <a href={`/magazine/read/${it.slug}`} target="_blank" rel="noreferrer" className="block">
                <CoverThumb
                  src={it.coverKey ? `${it.publicUrl?.replace(/\/$/, "") || ""}/${it.coverKey}` : ""}
                  coverKey={it.coverKey}
                  publicUrl={it.publicUrl}
                  className="w-full"
                  width={240}
                  title={it.title}
                />
              </a>
              <div className="p-2 md:p-3 space-y-2">
                {editing[it.slug] ? (
                  <>
                    <input className="admin-input w-full border rounded px-2 py-1 text-sm text-black" value={editing[it.slug].title} onChange={(e) => setEditing((s) => ({ ...s, [it.slug]: { ...s[it.slug], title: e.target.value } }))} />
                    <input type="date" className="admin-input w-full border rounded px-2 py-1 text-sm text-black" value={editing[it.slug].date} onChange={(e) => setEditing((s) => ({ ...s, [it.slug]: { ...s[it.slug], date: e.target.value } }))} />
                    <div className="text-sm text-neutral-700">Cover image (optional)</div>
                    <input type="file" accept="image/jpeg,image/png,image/webp" onChange={(e) => setEditing((s) => ({ ...s, [it.slug]: { ...s[it.slug], coverFile: e.target.files?.[0] || null, coverUploadPct: 0 } }))} />
                    {typeof editing[it.slug]?.coverUploadPct === 'number' && editing[it.slug].coverUploadPct > 0 && (
                      <div className="w-full h-2 bg-neutral-200 rounded overflow-hidden">
                        <div className="h-full bg-indigo-600 transition-[width] duration-200" style={{ width: `${editing[it.slug].coverUploadPct}%` }} />
                      </div>
                    )}
                    <div className="flex gap-2 pt-1">
                      <button className="border rounded px-2 py-1 hover:bg-neutral-50 cursor-pointer" onClick={() => commitEdit(it)}>Save</button>
                      <button className="border rounded px-2 py-1 hover:bg-neutral-50 cursor-pointer" onClick={() => cancelEdit(it.slug)}>Cancel</button>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="font-medium leading-snug text-black text-sm md:text-base">{it.title}</div>
                    <div className="text-xs md:text-sm text-neutral-600">
                      {it.publishedAt ? formatInputDateUTC(it.publishedAt) : "—"}
                      {it.pageCount > 0 && <span className="ml-2">• {it.pageCount} pages</span>}
                    </div>
                    <div className="flex flex-wrap gap-2 pt-1">
                      <button className="border rounded px-2 py-1 text-xs hover:bg-neutral-50 cursor-pointer" onClick={() => startEdit(it)}>Edit</button>
                      <button className="border rounded px-2 py-1 text-xs hover:bg-blue-50 text-blue-700 cursor-pointer" onClick={() => openReplace(it)}>Replace PDF</button>
                      {it.webpKeys?.length > 0 && (
                        <button className="border rounded px-2 py-1 text-xs hover:bg-purple-50 text-purple-700 cursor-pointer" onClick={() => openPageReplace(it)}>Replace Page</button>
                      )}
                      <button className="border rounded px-2 py-1 text-xs text-red-700 hover:bg-red-50 cursor-pointer" onClick={() => onDelete(it.slug)}>Delete</button>
                    </div>
                  </>
                )}
              </div>
            </article>
          )) : (
            <div className="text-sm text-neutral-700">No items</div>
          )}
        </div>
      </section>

      {/* Delete confirm modal */}
      {confirmSlug && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/50">
          <div className="bg-white rounded-md p-5 w-full max-w-sm">
            <h3 className="font-medium text-lg mb-2">Delete magazine</h3>
            <p className="text-sm text-neutral-700 mb-4">Are you sure you want to delete "{items.find(i=>i.slug===confirmSlug)?.title || confirmSlug}"? This cannot be undone.</p>
            {deleting && (
              <div className="mb-4 flex items-center gap-2 text-sm text-neutral-600">
                <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span>Deleting magazine and files...</span>
              </div>
            )}
            <div className="flex justify-end gap-2">
              <button className="border rounded px-3 py-1 disabled:opacity-50 cursor-pointer" disabled={deleting} onClick={() => setConfirmSlug(null)}>Cancel</button>
              <button className="border rounded px-3 py-1 bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 cursor-pointer" disabled={deleting} onClick={doDelete}>
                {deleting ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Replace modal */}
      {replaceOpen && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg overflow-hidden">
            <div className="px-5 py-4 border-b flex items-center justify-between">
              <h3 className="text-lg font-semibold text-black">Replace PDF</h3>
              <button className="text-neutral-600 hover:text-black cursor-pointer" aria-label="Close" onClick={closeReplace}>
                <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"/></svg>
              </button>
            </div>
            <div className="px-5 py-4 grid gap-3">
              <div className="text-sm text-neutral-700">Upload a new PDF for this magazine. The old file will be removed after replacement.</div>
              <label className="grid gap-1">
                <span className="text-sm text-neutral-700">PDF File</span>
                <input type="file" className="text-black" accept="application/pdf" onChange={(e) => setReplaceForm((s) => ({ ...s, file: e.target.files?.[0] || null }))} />
                {replaceForm.file && <div className="text-xs text-neutral-600 truncate">{replaceForm.file.name} ({Math.round((replaceForm.file.size||0)/1024/1024*10)/10} MB)</div>}
              </label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <label className="grid gap-1">
                  <span className="text-sm text-neutral-700">Title</span>
                  <input className="w-full border rounded px-3 py-2 text-black" value={replaceForm.title} onChange={(e) => setReplaceForm((s) => ({ ...s, title: e.target.value }))} />
                </label>
                <label className="grid gap-1">
                  <span className="text-sm text-neutral-700">Publish Date</span>
                  <input type="date" className="w-full border rounded px-3 py-2 text-black" value={replaceForm.date} onChange={(e) => setReplaceForm((s) => ({ ...s, date: e.target.value }))} />
                </label>
              </div>
              {typeof replaceForm.pct === 'number' && replaceForm.pct > 0 && (
                <div className="w-full h-2 bg-neutral-200 rounded overflow-hidden">
                  <div className="h-full bg-[#4cc8f4] transition-[width] duration-200" style={{ width: `${replaceForm.pct}%` }} />
                </div>
              )}
              {replaceForm.err && <div className="text-sm text-red-600">{replaceForm.err.message || String(replaceForm.err)}</div>}
            </div>
            <div className="px-5 py-4 border-t flex justify-end gap-2 bg-neutral-50">
              <button className="border rounded px-3 py-2 cursor-pointer" onClick={closeReplace}>Cancel</button>
              <button className="rounded px-3 py-2 text-white disabled:opacity-50 cursor-pointer" style={{ backgroundColor: '#FF9843' }} disabled={!replaceForm.file} onClick={confirmReplace}>
                {replaceForm.pct > 0 && replaceForm.pct < 100 ? `Uploading… ${replaceForm.pct}%` : 'Confirm Replace'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Replace single page modal */}
      {pageReplaceOpen && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg overflow-hidden">
            <div className="px-5 py-4 border-b flex items-center justify-between">
              <h3 className="text-lg font-semibold text-black">Replace Single Page</h3>
              <button className="text-neutral-600 hover:text-black cursor-pointer" aria-label="Close" onClick={closePageReplace}>
                <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"/></svg>
              </button>
            </div>
            <div className="px-5 py-4 grid gap-3">
              <div className="text-sm text-neutral-700">
                Replace a single page in <span className="font-semibold">{pageReplaceForm.title}</span>
                {pageReplaceForm.pageCount > 0 && <span> ({pageReplaceForm.pageCount} pages total)</span>}
              </div>
              <label className="grid gap-1">
                <span className="text-sm text-neutral-700">Page Number (1-{pageReplaceForm.pageCount || 1})</span>
                <input 
                  type="number" 
                  min="1" 
                  max={pageReplaceForm.pageCount || 1}
                  className="w-full border rounded px-3 py-2 text-black" 
                  value={pageReplaceForm.pageNumber} 
                  onChange={(e) => setPageReplaceForm((s) => ({ ...s, pageNumber: parseInt(e.target.value, 10) || 1 }))} 
                />
              </label>
              <label className="grid gap-1">
                <span className="text-sm text-neutral-700">WebP Image File</span>
                <input 
                  type="file" 
                  className="text-black" 
                  accept="image/webp" 
                  onChange={(e) => setPageReplaceForm((s) => ({ ...s, file: e.target.files?.[0] || null }))} 
                />
                {pageReplaceForm.file && (
                  <div className="text-xs text-neutral-600 truncate">
                    {pageReplaceForm.file.name} ({Math.round((pageReplaceForm.file.size||0)/1024)} KB)
                  </div>
                )}
              </label>
              {typeof pageReplaceForm.pct === 'number' && pageReplaceForm.pct > 0 && (
                <div className="space-y-1">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-neutral-600">{pageReplaceForm.status || "Uploading..."}</span>
                    <span className="text-neutral-500">{pageReplaceForm.pct}%</span>
                  </div>
                  <div className="w-full h-2 bg-neutral-200 rounded overflow-hidden">
                    <div className="h-full bg-purple-600 transition-[width] duration-200" style={{ width: `${pageReplaceForm.pct}%` }} />
                  </div>
                </div>
              )}
              {pageReplaceForm.err && <div className="text-sm text-red-600">{pageReplaceForm.err.message || String(pageReplaceForm.err)}</div>}
            </div>
            <div className="px-5 py-4 border-t flex justify-end gap-2 bg-neutral-50">
              <button className="border rounded px-3 py-2 cursor-pointer" onClick={closePageReplace}>Cancel</button>
              <button 
                className="rounded px-3 py-2 text-white disabled:opacity-50 cursor-pointer" 
                style={{ backgroundColor: '#9333ea' }} 
                disabled={!pageReplaceForm.file || pageReplaceForm.pageNumber < 1 || pageReplaceForm.pageNumber > pageReplaceForm.pageCount} 
                onClick={confirmPageReplace}
              >
                {pageReplaceForm.pct > 0 && pageReplaceForm.pct < 100 ? `Uploading… ${pageReplaceForm.pct}%` : 'Replace Page'}
              </button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}
